/**
 * useXero.js — Xero integration hooks
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { logAction } from '../lib/actionLogger';

/**
 * Check Xero connection status.
 */
export function useXeroStatus() {
  return useQuery({
    queryKey: ['xero_status'],
    queryFn: () => api.get('/xero/status'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Disconnect Xero integration.
 */
export function useXeroDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/xero/disconnect'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['xero_status'] });
      logAction({
        action: 'Disconnect Xero',
        description: 'Disconnected Xero integration',
      });
    },
  });
}

/**
 * Fetch revenue accounts from Xero.
 */
export function useXeroAccounts(enabled = true) {
  return useQuery({
    queryKey: ['xero_accounts'],
    queryFn: () => api.get('/xero/accounts'),
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Browse Xero ACCREC invoices available to link (excludes already-linked).
 */
export function useXeroInvoices(search = '', { enabled = false } = {}) {
  return useQuery({
    queryKey: ['xero_invoices', search],
    queryFn: () => api.get(`/xero/invoices${search ? `?search=${encodeURIComponent(search)}` : ''}`),
    enabled,
    staleTime: 60 * 1000,
  });
}

/**
 * Link a Xero-created invoice to a job (creates an RFOS invoice row carrying
 * the Xero id; the poller then reconciles payments).
 */
export function useLinkXeroInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ xeroInvoiceId, jobId, invoiceType }) =>
      api.post(`/xero/link-invoice/${xeroInvoiceId}`, { jobId, invoiceType }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['xero_invoices'] });
      if (data?.job_id || data?.jobId) {
        const jid = data.job_id || data.jobId;
        qc.invalidateQueries({ queryKey: ['invoices', 'job', jid] });
      }
      logAction({
        action: 'Link Xero Invoice',
        description: `Linked Xero invoice ${data?.number} (${data?.client}) to job — ${data?.invoice_type}, ${data?.amount}`,
        details: {
          number: data?.number,
          client: data?.client,
          invoiceType: data?.invoice_type,
          amount: data?.amount,
          status: data?.status,
          xeroInvoiceId: data?.xero_invoice_id,
          jobId: data?.job_id,
        },
        jobId: data?.job_id || undefined,
      });
    },
  });
}

// The global defaults retry queries 2x and mutations 1x (see queryClient.js).
// That is wrong for Xero: a 503 here means "the tenant is over its API quota",
// which retrying cannot fix — it just multiplies one throttled call into
// three. Honour it as the terminal answer it is.
const retryUnlessRateLimited = (max) => (failureCount, error) =>
  error?.status !== 503 && failureCount < max;

/**
 * Push an invoice to Xero.
 */
export function useSyncInvoiceToXero() {
  const qc = useQueryClient();
  return useMutation({
    retry: retryUnlessRateLimited(1),
    mutationFn: (invoiceId) => api.post(`/xero/sync-invoice/${invoiceId}`),
    onSuccess: (data, invoiceId) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      logAction({
        action: 'Sync Invoice to Xero',
        description: `Pushed invoice ${invoiceId} to Xero (Xero id ${data?.xeroInvoiceId})`,
        details: {
          invoiceId,
          xeroInvoiceId: data?.xeroInvoiceId,
          xeroOnlineUrl: data?.xeroOnlineUrl,
        },
      });
    },
  });
}

/**
 * Check for new payments from Xero on a synced invoice.
 */
export function useCheckXeroPayments(invoiceId, { enabled = false } = {}) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ['xero_payments', invoiceId],
    queryFn: async () => {
      const result = await api.get(`/xero/check-payments/${invoiceId}`);
      if (result.updated) {
        qc.invalidateQueries({ queryKey: ['invoices'] });
      }
      return result;
    },
    enabled: !!invoiceId && enabled,
    staleTime: 2 * 60 * 1000,
    retry: retryUnlessRateLimited(2),
  });
}

/**
 * Check all synced unpaid invoices for a job against Xero.
 */
export function useCheckXeroPaymentsByJob(jobId, { enabled = false } = {}) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ['xero_payments_job', jobId],
    queryFn: async () => {
      const result = await api.get(`/xero/check-payments-by-job/${jobId}`);
      if (result.updated?.length) {
        qc.invalidateQueries({ queryKey: ['invoices'] });
      }
      return result;
    },
    enabled: !!jobId && enabled,
    staleTime: 2 * 60 * 1000,
    retry: retryUnlessRateLimited(2),
  });
}

/**
 * Refresh from Xero: pull open invoices, then report which jobs have a status
 * their invoices no longer support. Proposes only — nothing is written until
 * useApplyReconcile runs, because a job reaching 'paid' releases crew payouts.
 */
export function useXeroReconcile() {
  const qc = useQueryClient();
  return useMutation({
    // Never retry: each attempt spends one Xero call per open invoice, and a
    // 503 here means the tenant is over quota, which retrying cannot fix.
    retry: false,
    mutationFn: () => api.post('/xero/reconcile'),
    onSuccess: (r) => {
      // The Xero lib writes invoices and jobs with bare pool.query and never
      // emits db-change, so realtime does not cover this. Invalidate manually
      // or the list keeps rendering pre-sweep data.
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      qc.invalidateQueries({ queryKey: ['xero_status'] });
      logAction({
        action: 'Xero Reconcile',
        description: `Checked ${r?.xero?.scanned ?? 0} invoice(s) — ${r?.xero?.updated ?? 0} updated, `
          + `${r?.jobs?.proposals?.length ?? 0} job status change(s) proposed`,
        details: { xero: r?.xero, proposals: r?.jobs?.proposals, unsynced: r?.unsynced?.length, xeroOnly: r?.xeroOnly?.length },
      });
    },
  });
}

/**
 * Commit chosen job-status transitions from a reconcile preview.
 * The server re-derives every proposal, so a stale preview is skipped, not forced.
 */
export function useApplyReconcile() {
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: (transitions) => api.post('/xero/reconcile/apply', { transitions }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      qc.invalidateQueries({ queryKey: queryKeys.actionLog.all });
      logAction({
        action: 'Xero Reconcile Apply',
        description: (r?.applied || []).map((a) => `${a.title}: ${a.from} → ${a.to}`).join('; ')
          || 'No job statuses changed',
        details: r,
      });
    },
  });
}

/**
 * Upload a PDF as an attachment on a Xero-synced invoice.
 * With IncludeOnline=true (default), the PDF becomes the download shown
 * on the public online-invoice / payment page — replacing Xero's default PDF.
 */
export function useUploadInvoicePdfToXero() {
  return useMutation({
    mutationFn: async ({ invoiceId, blob, filename }) => {
      const qs = new URLSearchParams();
      if (filename) qs.set('filename', filename);
      const res = await fetch(`/api/xero/upload-invoice-pdf/${invoiceId}?${qs}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/pdf' },
        body: blob,
      });
      if (!res.ok) {
        const text = await res.text();
        let msg;
        try { msg = JSON.parse(text).error || text; } catch { msg = text || `Upload failed (${res.status})`; }
        throw new Error(msg);
      }
      return res.json();
    },
    onSuccess: (data, variables) => {
      const filename = data?.filename || variables?.filename;
      logAction({
        action: 'Upload Invoice PDF to Xero',
        description: `Uploaded branded PDF '${filename}' to Xero invoice ${variables?.invoiceId} (public payment-page attachment)`,
        details: {
          invoiceId: variables?.invoiceId,
          filename,
          attachmentId: data?.attachmentId,
        },
      });
    },
  });
}
